import { memo, useEffect, useState } from 'react'
import { css } from '@emotion/css'
import { useMutation, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Bell, MailWarningIcon } from 'lucide-react'

import {
  getGlobalTriggerState,
  toggleTriggerEnabled,
} from '~/dataProcessor/api/api'
import { Switch } from '~/design-system/atom/switch'
import { ToolTipBox } from '~/design-system/atom/tooltip'
import { useToast } from '~/design-system/atom/use-toast'
import { deleteOrgProjectWebhook } from '~/generated/client'
import {
  useGetTriggerWebhookURL,
  useUpdateWebhookURL,
} from '~/hooks/query/settings'
import { useGetUserInfo } from '~/ui/containers/base/hooks'
import { validateURL } from '~/utils/urlUtils'
import { TriggerWebhookSection } from './components/TriggerWebhook'
import { ApprovalPopup } from './settings'

export const TriggerWebhookSettingBlock = memo(() => {
  const { toast } = useToast()
  const { data, isLoading, isError } = useGetTriggerWebhookURL('trigger')
  const { url } = data ?? {}
  const [inputCallbackURL, setInputCallbackURL] = useState('')
  const [stagedCallbackURL, setStagedCallbackURL] = useState<string | null>(
    null,
  )
  const [isEditing, setIsEditing] = useState(false)

  const { authSessionInfo: clientInfo, isLoading: isClientInfoLoading } =
    useGetUserInfo()

  const triggerWebhookURL =
    url?.type === 'trigger' ? url.webhook_url : undefined

  const isNewWebhookEnabled = clientInfo?.project?.is_new_webhook

  useEffect(() => {
    if (triggerWebhookURL) {
      setInputCallbackURL(triggerWebhookURL)
    }
  }, [triggerWebhookURL, isLoading])

  const { mutate: updateWebhookURLMutation } = useUpdateWebhookURL()

  const { mutate: deleteWebhookMutation } = useMutation({
    mutationFn: () => {
      return deleteOrgProjectWebhook({
        requestBody: {
          type: 'trigger',
        },
      })
    },
    onSuccess: () => {
      setStagedCallbackURL(null)
      setInputCallbackURL('')
      setIsEditing(false)
      toast({
        title: 'Success',
        description: 'Webhook URL was deleted successfully',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error deleting webhook URL',
        variant: 'destructive',
      })
    },
  })

  const handleUpdateOfCallbackURL = async () => {
    if (stagedCallbackURL !== null) {
      if (stagedCallbackURL === '') {
        // delete the webhook
        deleteWebhookMutation()
      } else {
        updateWebhookURLMutation(
          {
            type: 'trigger',
            webhookURL: stagedCallbackURL,
          },
          {
            onSuccess: () => {
              setIsEditing(false)
            },
          },
        )
        setStagedCallbackURL(null)
        setInputCallbackURL(stagedCallbackURL)
      }
    } else {
      updateWebhookURLMutation(
        {
          type: 'trigger',
          webhookURL: inputCallbackURL,
        },
        {
          onSuccess: () => {
            setIsEditing(false)
          },
        },
      )
    }
  }

  if (isError) {
    return null
  }

  if (isClientInfoLoading || isLoading) {
    return (
      <div className="m-auto flex w-[900px] flex-col items-start">
        <div role="status" className="w-full animate-pulse">
          <div className="mb-4 h-2.5 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 max-w-[330px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="mb-2.5 h-2 max-w-[300px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="m-auto flex w-[900px] flex-col items-start">
      {stagedCallbackURL !== null && (
        <ApprovalPopup
          heading={
            stagedCallbackURL === ''
              ? 'Are you sure about delete the webhook URL?'
              : 'Are you sure about updating the webhook URL?'
          }
          description={
            'This can potentially break integrations that rely on this webhook.'
          }
          approveButtonText={
            stagedCallbackURL === ''
              ? 'Proceed with delete'
              : 'Proceed with update'
          }
          onApprove={async () => {
            await handleUpdateOfCallbackURL()
          }}
          setOpen={() => {
            setStagedCallbackURL(null)
          }}
        />
      )}
      <div className="items-base flex w-full flex-col justify-between">
        <div className=" text-[16px] font-[500] text-black-200">
          Events and Triggers
        </div>
        <span className="mb-2 mt-1 flex w-full items-center text-[13px] text-grey-900">
          Configure webhook endpoints and manage event notifications
        </span>
      </div>

      <hr className="my-2 mt-2 h-[1px] w-full bg-grey-300" />
      <div className="mt-4 w-full">
        <TriggerWarningState />
        <TriggerWebhookSection
          icon={<Bell className="h-5 w-5" />}
          title="Trigger Webhook URL"
          description="Receive trigger events for connected accounts and apps as JSON Payload"
          webhookUrl={inputCallbackURL}
          onChange={setInputCallbackURL}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          onDelete={() => {
            setStagedCallbackURL('')
          }}
          onSave={() => {
            if (!validateURL(inputCallbackURL) && stagedCallbackURL === null) {
              toast({
                title: 'Error',
                description: 'Please enter a valid event webhook URL',
                variant: 'destructive',
              })
              return
            }
            setStagedCallbackURL(inputCallbackURL)
          }}
        >
          <div className="mt-8 flex">
            <span className="mr-4 self-center font-[500] text-black-200">
              Use New Webhook Payload
            </span>
            <ToolTipBox
              content={
                'All triggers are now new format, the old format is deprecated and will be removed in the future, if you have an old trigger contact support@composio.dev to migrate'
              }
            >
              <Switch
                checked={isNewWebhookEnabled}
                disabled={true}
                checkedColor="#8123CD"
              />
            </ToolTipBox>
          </div>
        </TriggerWebhookSection>
      </div>
    </div>
  )
})

const TriggerWarningState = memo(() => {
  const [showWarning, setShowWarning] = useState(false)
  const {
    data: triggersInfo,
    refetch,
    isLoading,
  } = useQuery(['triggerState'], getGlobalTriggerState, {})

  console.log('triggersInfo', triggersInfo)

  useEffect(() => {
    if (!triggersInfo?.triggers_enabled) {
      setShowWarning(true)
    } else {
      setShowWarning(false)
    }
  }, [triggersInfo?.triggers_enabled])

  const { toast } = useToast()

  const setTriggerState = (newState: boolean) => {
    toggleTriggerEnabled(newState)
      .then(() => {
        refetch()
        toast({
          title: 'Success',
          description: `Triggers ${newState ? 'enabled' : 'disabled'} successfully`,
          variant: 'success',
        })
      })
      .catch((err) => {
        toast({
          title: 'Error',
          description: `Error occurred while ${newState ? 'enabling' : 'disabling'} triggers`,
          variant: 'destructive',
        })
        throw err
      })
  }

  if (!showWarning || isLoading) {
    return null
  }

  return (
    <div className="mb-6 w-full rounded-lg border border-grey-200 p-5">
      <div>
        <div className=" flex items-center gap-2  text-[16px] font-[500] text-[#A40C4D]">
          <MailWarningIcon height={16} width={16} />
          Webhook was disabled due to delivery failures
        </div>
        <div className="mt-2 flex items-center gap-2 text-black-200">
          Triggers are disabled after 24 hours of inactivity, i.e all the events
          are failing to be delivered.{' '}
        </div>
      </div>
      <div className="mt-5 flex  items-center justify-between  gap-2  text-[8px] font-[500]">
        <div className="flex items-center gap-2 text-[14px] font-[500] text-black-200">
          <div className="flex items-center gap-2">
            I have fixed the error and want to re-enable webhook delivery{' '}
          </div>
        </div>
        <div className="text-[12px] font-[500] text-grey-1000 underline">
          <Switch
            disabled={false}
            checked={triggersInfo?.triggers_enabled}
            className={clsx(
              'h-[18px] w-[40px]',
              css`
                span {
                  width: 12px;
                  height: 12px;
                }
              `,
            )}
            onCheckedChange={() => {
              setTriggerState(!triggersInfo?.triggers_enabled)
            }}
          />
        </div>
      </div>
    </div>
  )
})
